import { useEffect, useState } from 'react';
import { T } from '../theme';
import QbrSetupStep from './qbr/QbrSetupStep';
import QbrDataStep from './qbr/QbrDataStep';
import QbrPreviewStep from './qbr/QbrPreviewStep';
import QbrGenerateStep from './qbr/QbrGenerateStep';
import { listQbrRuns } from '../db';

// QBR Generator — top-level workspace.
//
// The flow runs in 4 steps: setup → data → preview → generate. Each step
// gets its own component; QbrScreen owns the orchestration state and the
// transition between them. A run history strip on the right lists prior
// QBRs for the same project (downloadable when complete).
const STEPS = ['setup', 'data', 'preview', 'generate'];
const STEP_LABELS = { setup: 'Setup', data: 'Data', preview: 'Preview', generate: 'Generate' };

export default function QbrScreen({ project, currentProfile }) {
  const [step, setStep] = useState('setup');
  const [setupResult, setSetupResult] = useState(null);
  const [dataResult, setDataResult] = useState(null);
  const [previewResult, setPreviewResult] = useState(null);
  const [runs, setRuns] = useState([]);

  useEffect(() => {
    if (!project?.id) return;
    let cancelled = false;
    listQbrRuns(project.id).then(rs => { if (!cancelled) setRuns(rs || []); });
    return () => { cancelled = true; };
  }, [project?.id]);

  // Allow jumping back to an already-completed step (results stay in state).
  const goToStep = (target) => {
    if (STEPS.indexOf(target) <= STEPS.indexOf(step)) setStep(target);
  };

  return (
    <div style={{ display: 'flex', gap: 16, alignItems: 'flex-start' }}>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ background: T.white, border: `1px solid ${T.borderSubtle}`, borderRadius: T.radiusCard, padding: 24 }}>
          <Header project={project} step={step} onStep={goToStep} meta={setupResult?.quarter_label} />

          {step === 'setup' && (
            <QbrSetupStep
              project={project}
              onContinue={result => { setSetupResult(result); setStep('data'); }}
            />
          )}

          {step === 'data' && setupResult && (
            <QbrDataStep
              setup={setupResult}
              project={project}
              onBack={() => setStep('setup')}
              onContinue={result => { setDataResult(result); setStep('preview'); }}
            />
          )}

          {step === 'preview' && dataResult && (
            <QbrPreviewStep
              project={project}
              setup={setupResult}
              dataResult={dataResult}
              currentProfile={currentProfile}
              onBack={() => setStep('data')}
              onContinue={result => { setPreviewResult(result); setStep('generate'); }}
            />
          )}

          {step === 'generate' && dataResult && previewResult && (
            <QbrGenerateStep
              project={project}
              setup={setupResult}
              dataResult={dataResult}
              previewResult={previewResult}
              currentProfile={currentProfile}
              onBack={() => setStep('preview')}
            />
          )}
        </div>
      </div>

      <RunsStrip runs={runs} />
    </div>
  );
}

function Header({ project, step, onStep, meta }) {
  return (
    <div style={{ marginBottom: 20 }}>
      <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 16, flexWrap: 'wrap' }}>
        <div>
          <h2 style={{ font: `700 18px ${T.fontSans}`, color: T.ink, margin: 0 }}>Quarterly Business Review</h2>
          <p style={{ font: `400 13px ${T.fontSans}`, color: T.inkMuted, margin: '4px 0 0' }}>
            Auto-generate the QBR deck for <strong>{project?.name || 'this project'}</strong> from FMX data.
          </p>
        </div>
        {meta && <span style={{ font: `400 12px ${T.fontSans}`, color: T.inkSoft, whiteSpace: 'nowrap' }}>{meta}</span>}
      </div>
      {/* Internal 4-step stepper — same visual language as the wizard indicator */}
      <Stepper currentStep={step} onStep={onStep} />
    </div>
  );
}

function Stepper({ currentStep, onStep }) {
  const curIdx = STEPS.indexOf(currentStep);
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 0, marginTop: 16, overflowX: 'auto' }}>
      {STEPS.map((s, idx) => {
        const done = idx < curIdx;
        const cur = idx === curIdx;
        const clickable = idx <= curIdx && !cur;
        return (
          <div key={s} style={{ display: 'flex', alignItems: 'center' }}>
            <div onClick={() => clickable && onStep && onStep(s)} style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: clickable ? 'pointer' : 'default', whiteSpace: 'nowrap' }}>
              <span style={{
                width: 24, height: 24, borderRadius: T.radiusPill, flexShrink: 0,
                background: done ? T.green500 : cur ? T.brand : T.white,
                border: done || cur ? 'none' : `1.5px solid ${T.border}`,
                color: done || cur ? T.white : T.inkSoft,
                display: 'inline-flex', alignItems: 'center', justifyContent: 'center', font: `700 12px ${T.fontSans}`,
              }}>{done ? <i className="fa-solid fa-check" style={{ fontSize: 11 }} /> : idx + 1}</span>
              <span style={{ font: cur ? `700 14px ${T.fontSans}` : `500 14px ${T.fontSans}`, color: cur ? T.ink : done ? T.inkMuted : T.inkSoft }}>{STEP_LABELS[s]}</span>
            </div>
            {idx < STEPS.length - 1 && <span style={{ width: 40, height: 2, background: done ? T.green500 : T.borderSubtle, margin: '0 14px' }} />}
          </div>
        );
      })}
    </div>
  );
}

function RunsStrip({ runs }) {
  return (
    <div style={{ width: 240, flexShrink: 0, background: T.white, border: `1px solid ${T.borderSubtle}`, borderRadius: T.radiusCard, padding: 14 }}>
      <div style={{ font: `700 11px ${T.fontSans}`, color: T.inkMuted, textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 10 }}>
        Past QBRs
      </div>
      {(!runs || runs.length === 0) ? (
        <div style={{ font: `400 12px ${T.fontSans}`, color: T.inkMuted, fontStyle: 'italic' }}>
          No QBRs generated yet for this project.
        </div>
      ) : runs.map(r => (
        <div key={r.id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 0', borderBottom: `1px solid ${T.borderSubtle}` }}>
          <span style={{ width: 28, height: 28, borderRadius: T.radiusBtn, background: T.blue50, color: T.brand, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
            <i className="fa-solid fa-file-powerpoint" style={{ fontSize: 12 }} />
          </span>
          <div style={{ minWidth: 0 }}>
            <div style={{ font: `600 13px ${T.fontSans}`, color: T.ink, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{r.quarter_label}</div>
            <div style={{ font: `400 11px ${T.fontSans}`, color: T.inkSoft }}>
              {new Date(r.created_at).toLocaleDateString()} · {r.status}
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}
